import Snackbar from '@mui/material/Snackbar'
import {
    createTheme,
    THEME_ID as MATERIAL_THEME_ID,
    ThemeProvider as MaterialThemeProvider,
} from '@mui/material/styles'
import Button from '@mui/joy/Button'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'

import { getUpdatedSearchUrl } from '../helpers'
import {
    searchSubmissionAtom,
    suggestedStartTimeShiftAtom,
} from '../state'

export type SuggestedStartTimeSnackbarPlacement =
    | 'website-map'
    | 'website-detail'
    | 'pwa'

const bottomByPlacement: Record<SuggestedStartTimeSnackbarPlacement, string> = {
    'website-map': '5rem',
    'website-detail': '8.5rem',
    pwa: '10.75rem',
}

const materialTheme = createTheme()

export default function SuggestedStartTimeSnackbar({
    placement,
}: {
    placement: SuggestedStartTimeSnackbarPlacement,
}) {
    const { t } = useTranslation()
    const [searchSubmission, setSearchSubmission] = useAtom(searchSubmissionAtom)
    const [shift, setShift] = useAtom(suggestedStartTimeShiftAtom)

    const handleUndo = () => {
        if (!shift || searchSubmission !== shift.appliedSearch) {
            setShift(null)
            return
        }

        setSearchSubmission(shift.previousSearch)

        if (shift.urlVariant && !window.location.pathname.startsWith('/pwa')) {
            window.history.replaceState(
                {},
                '',
                getUpdatedSearchUrl({
                    currentUrl: window.location.href,
                    searchSubmission: shift.previousSearch,
                    variant: shift.urlVariant,
                    garageId: shift.garageId,
                }),
            )
        }
    }

    return (
        <MaterialThemeProvider theme={{ [MATERIAL_THEME_ID]: materialTheme }}>
            <Snackbar
                key={shift?.id ?? 'closed'}
                open={shift !== null && searchSubmission === shift.appliedSearch}
                autoHideDuration={6000}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                onClose={(_event, reason) => {
                    if (reason === 'clickaway') return
                    setShift(null)
                }}
                sx={{
                    '&&': {
                        bottom: bottomByPlacement[placement],
                        ...(placement === 'website-detail' ? {
                            '@media (min-width: 1230px)': {
                                bottom: '1.5rem',
                            },
                        } : {
                            '@media (min-width: 900px)': {
                                bottom: placement === 'pwa' ? '6.75rem' : '1.5rem',
                            },
                        }),
                    },
                }}
            >
                <Sheet
                    role="status"
                    aria-live="polite"
                    variant="solid"
                    color="neutral"
                    sx={{
                        width: '100%',
                        maxWidth: '30rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        py: 1,
                        px: 1.5,
                        borderRadius: '0.25rem',
                        backgroundColor: 'neutral.800',
                        boxShadow: 'lg',
                    }}
                >
                    <Typography level="body-sm" sx={{ color: 'common.white' }}>
                        {t('general.startTimeMoved', { time: shift?.startTime ?? '' })}
                    </Typography>
                    <Button
                        variant="plain"
                        color="secondary"
                        size="sm"
                        onClick={handleUndo}
                        sx={{ flexShrink: 0 }}
                    >
                        {t('general.undo')}
                    </Button>
                </Sheet>
            </Snackbar>
        </MaterialThemeProvider>
    )
}
